import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Privacy Policy',
  sections: [{
    heading: 'Information We Collect',
    body: "When you purchase The Grandparent's Smartphone Survival Guide, we collect only the information necessary to process your order, including your name, email address, and payment details. We do not collect any information from the end users of the guide."
  }, {
    heading: 'How We Use Your Information',
    body: 'Your information is used solely to deliver your digital purchase and send order confirmation. We will never sell, rent, or share your personal information with third parties for marketing purposes.'
  }, {
    heading: 'Data Security',
    body: 'We use industry-standard encryption and security measures to protect your personal information. Payment processing is handled by trusted third-party providers who comply with PCI-DSS standards.'
  }, {
    heading: 'Cookies',
    body: 'Our website may use essential cookies to ensure proper functionality. We do not use tracking cookies or third-party advertising cookies.'
  }, {
    heading: 'Your Rights',
    body: 'You have the right to request access to, correction of, or deletion of your personal data at any time. To exercise these rights, please reach out to us through our website.'
  }, {
    heading: 'Changes to This Policy',
    body: 'We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.'
  }]
};
const termsContent = {
  title: 'Terms of Service',
  sections: [{
    heading: 'Product Description',
    body: "The Grandparent's Smartphone Survival Guide is a 32-page digital PDF guide designed to help older adults learn to use their smartphones. The guide is delivered as an instant digital download upon purchase."
  }, {
    heading: 'Digital Delivery',
    body: 'Upon successful payment, you will receive immediate access to download the PDF guide. As this is a digital product, delivery is instant and does not require shipping.'
  }, {
    heading: 'Permitted Use',
    body: 'You may download, print, and use the guide for personal and family use. You may print copies for family members. You may not redistribute, resell, or commercially exploit the guide without written permission.'
  }, {
    heading: 'Refund Policy',
    body: 'Due to the digital nature of this product, all sales are final. If you experience any issues with your download, please contact us and we will ensure you receive your guide.'
  }, {
    heading: 'Disclaimer',
    body: 'The guide is provided for informational purposes. While we strive for accuracy, technology changes frequently and some instructions may vary depending on device model and software version.'
  }, {
    heading: 'Limitation of Liability',
    body: 'To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, or consequential damages arising from the use of this guide.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60 backdrop-blur-sm" onClick={onClose} data-id="element-63">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: [0.21, 0.47, 0.32, 0.98]
    }} className="bg-cream rounded-2xl shadow-warm-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-64">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-warmGray" data-id="element-65">
          <h2 className="text-2xl font-bold font-heading text-charcoal" data-id="element-66">
            {content.title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-warmGray transition-colors" aria-label="Close" data-id="element-67">
            <XIcon className="w-5 h-5 text-charcoal" data-id="element-68" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-8 py-6 space-y-6" data-id="element-69">
          {content.sections.map((section, i) => <div key={i} data-id="element-70">
              <h3 className="font-bold text-lg text-charcoal mb-2 font-heading" data-id="element-71">
                {section.heading}
              </h3>
              <p className="text-charcoal/80 leading-relaxed" data-id="element-72">{section.body}</p>
            </div>)}
          <p className="text-sm text-warmBrown/60 pt-4 border-t border-warmGray" data-id="element-73">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
          })}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-cream py-12 px-6 text-center" data-id="element-74">
        <div className="max-w-4xl mx-auto" data-id="element-75">
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-76">
            <button onClick={() => setActiveModal('privacy')} className="text-cream/60 text-sm hover:text-amber transition-colors" data-id="element-77">
              Privacy Policy
            </button>
            <span className="text-cream/30" data-id="element-78">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-cream/60 text-sm hover:text-amber transition-colors" data-id="element-79">
              Terms of Service
            </button>
          </div>
          <p className="text-cream/60 text-sm mb-4" data-id="element-80">
            © {new Date().getFullYear()} The Grandparent's Smartphone Survival
            Guide. All rights reserved.
          </p>
          <p className="text-cream/40 text-xs" data-id="element-81">
            Helping families connect, one tap at a time.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-82">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-83" />}
      </AnimatePresence>
    </>;
}